'use client';

/**
 * RateCalculatorPanel Component
 *
 * Interactive rate calculator for loan intake wizard.
 * Shows estimated rate, LTV, LTC, and monthly payment.
 *
 * @see Issue #432 - Templated Loan Intake Wizard
 */

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { IntakeRateCalculatorConfig } from '@ops-matrix/shared-types';
import { calculateLoanRate, type RateCalculationInput } from '@ops-matrix/shared-types';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import { formatCurrencyWhole, formatRate } from '../../../utils/formatters';
import { MagicInput } from '../../MagicInput';
import { MagicSelect } from '../../MagicSelect';

// ============================================================================
// Types
// ============================================================================

export interface RateCalculatorPanelProps {
  /** Rate calculator configuration */
  config: IntakeRateCalculatorConfig;
  /** Current form data */
  formData: Record<string, unknown>;
  /** Change handler */
  onChange: (name: string, value: unknown) => void;
  /** Field errors */
  errors?: Record<string, string>;
  /** Whether fields are disabled */
  disabled?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

const ResultsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  background:
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
  borderRadius: 16,
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
}));

const MetricBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: theme.palette.primary.main,
  lineHeight: 1.2,
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.75rem',
  },
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
}));

const GaugeContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  marginTop: theme.spacing(1),
}));

interface GaugeFillProps {
  $percentage: number;
  $color: string;
}

const GaugeFill = styled(Box, {
  shouldForwardProp: (prop) => typeof prop === 'string' && !prop.startsWith('$'),
})<GaugeFillProps>(({ $percentage, $color }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  width: `${Math.min($percentage, 100)}%`,
  backgroundColor: $color,
  borderRadius: 4,
  transition: 'width 0.3s ease',
}));

const WarningText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.875rem',
  marginTop: theme.spacing(2),
  textAlign: 'center',
}));

const Disclaimer = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : theme.palette.text.secondary,
  marginTop: theme.spacing(2),
  textAlign: 'center',
  fontStyle: 'italic',
}));

const AdjustmentItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0.5, 0),
  fontSize: '0.875rem',
}));

// ============================================================================
// Constants
// ============================================================================

/** Field names consumed by RateCalculatorPanel — used by IntakeWizard to identify extra fields. */
export const RATE_CALCULATOR_FIELD_NAMES = new Set([
  'purchasePrice',
  'rehabBudget',
  'afterRepairValue',
  'requestedLoanAmount',
  'estimatedFico',
  'preferredTerm',
  'flipsInLast2Years',
]);

// ============================================================================
// Component
// ============================================================================

export const RateCalculatorPanel: React.FC<RateCalculatorPanelProps> = ({
  config,
  formData,
  onChange,
  errors = {},
  disabled = false,
}) => {
  const theme = useTheme();

  // Extract values from form data
  const purchasePrice = Number(formData['purchasePrice']) || 0;
  const rehabBudget = Number(formData['rehabBudget']) || 0;
  const afterRepairValue = Number(formData['afterRepairValue']) || 0;
  const requestedLoanAmount = Number(formData['requestedLoanAmount']) || 0;
  const estimatedFico = String(formData['estimatedFico'] || '');
  const preferredTerm = Number(formData['preferredTerm']) || config.termOptions[0] || 12;
  const flipsInLast2Years = String(formData['flipsInLast2Years'] || '');

  // Initialize default preferredTerm into formData so validation sees it
  useEffect(() => {
    if (
      formData['preferredTerm'] === undefined ||
      formData['preferredTerm'] === null ||
      formData['preferredTerm'] === ''
    ) {
      const defaultTerm = config.termOptions[0] || 12;
      onChange('preferredTerm', String(defaultTerm));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only on mount

  // Calculate rate
  const calculation = useMemo(() => {
    if (!purchasePrice || !afterRepairValue || !requestedLoanAmount) {
      return null;
    }

    const input: RateCalculationInput = {
      purchasePrice,
      rehabBudget,
      afterRepairValue,
      requestedLoanAmount,
      estimatedFico,
      preferredTerm,
      flipsInLast2Years,
      maxLTV: config.maxLTV,
      maxLTC: config.maxLTC,
      defaultRate: config.defaultRate,
    };

    return calculateLoanRate(input);
  }, [
    purchasePrice,
    rehabBudget,
    afterRepairValue,
    requestedLoanAmount,
    estimatedFico,
    preferredTerm,
    flipsInLast2Years,
    config,
  ]);

  // Get gauge color based on percentage
  const getGaugeColor = (value: number, max: number): string => {
    const percentage = (value / max) * 100;
    if (percentage > 100) return theme.palette.error.main;
    if (percentage > 90) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const ficoOptions = [
    { value: '760+', label: '760+' },
    { value: '740-759', label: '740-759' },
    { value: '720-739', label: '720-739' },
    { value: '700-719', label: '700-719' },
    { value: '680-699', label: '680-699' },
    { value: '660-679', label: '660-679' },
    { value: 'below-660', label: 'Below 660' },
  ];

  const termOptions = config.termOptions.map((term) => ({
    value: String(term),
    label: term >= 12 ? `${term / 12} year${term > 12 ? 's' : ''}` : `${term} months`,
  }));

  return (
    <Box>
      {/* Input Fields */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicInput
            label="Purchase Price"
            value={purchasePrice ? String(purchasePrice) : ''}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, '');
              onChange('purchasePrice', val ? Number(val) : undefined);
            }}
            placeholder="$0"
            error={Boolean(errors['purchasePrice'])}
            helperText={errors['purchasePrice']}
            disabled={disabled}
            required
            slotProps={{
              input: {
                startAdornment: <Box sx={{ mr: 1, color: 'text.secondary' }}>$</Box>,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicInput
            label="Rehab Budget"
            value={rehabBudget ? String(rehabBudget) : ''}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, '');
              onChange('rehabBudget', val ? Number(val) : 0);
            }}
            placeholder="$0"
            error={Boolean(errors['rehabBudget'])}
            helperText={errors['rehabBudget'] || 'Estimated renovation costs'}
            disabled={disabled}
            slotProps={{
              input: {
                startAdornment: <Box sx={{ mr: 1, color: 'text.secondary' }}>$</Box>,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicInput
            label="After Repair Value (ARV)"
            value={afterRepairValue ? String(afterRepairValue) : ''}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, '');
              onChange('afterRepairValue', val ? Number(val) : undefined);
            }}
            placeholder="$0"
            error={Boolean(errors['afterRepairValue'])}
            helperText={errors['afterRepairValue'] || 'Estimated value after improvements'}
            disabled={disabled}
            required
            slotProps={{
              input: {
                startAdornment: <Box sx={{ mr: 1, color: 'text.secondary' }}>$</Box>,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicInput
            label="Requested Loan Amount"
            value={requestedLoanAmount ? String(requestedLoanAmount) : ''}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, '');
              onChange('requestedLoanAmount', val ? Number(val) : undefined);
            }}
            placeholder="$0"
            error={Boolean(errors['requestedLoanAmount'])}
            helperText={errors['requestedLoanAmount']}
            disabled={disabled}
            required
            slotProps={{
              input: {
                startAdornment: <Box sx={{ mr: 1, color: 'text.secondary' }}>$</Box>,
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicSelect
            label="Estimated Credit Score"
            value={estimatedFico}
            onChange={(e) => onChange('estimatedFico', e.target.value)}
            options={ficoOptions}
            error={Boolean(errors['estimatedFico'])}
            helperText={errors['estimatedFico']}
            disabled={disabled}
            required
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicSelect
            label="Preferred Loan Term"
            value={String(preferredTerm)}
            onChange={(e) => onChange('preferredTerm', e.target.value)}
            options={termOptions}
            error={Boolean(errors['preferredTerm'])}
            helperText={errors['preferredTerm']}
            disabled={disabled}
            required
          />
        </Grid>
      </Grid>
      {/* Results Display */}
      {calculation && (
        <ResultsCard elevation={0}>
          {calculation.isEligible ? (
            <>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <MetricBox>
                    <MetricValue>{formatRate(calculation.estimatedRate)}</MetricValue>
                    <MetricLabel>Estimated Rate</MetricLabel>
                  </MetricBox>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <MetricBox>
                    <MetricValue>{formatCurrencyWhole(calculation.monthlyPayment)}</MetricValue>
                    <MetricLabel>Monthly Payment</MetricLabel>
                  </MetricBox>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <MetricBox>
                    <MetricValue sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {formatRate(calculation.ltv)}
                    </MetricValue>
                    <MetricLabel>LTV (max {config.maxLTV}%)</MetricLabel>
                    <GaugeContainer>
                      <GaugeFill
                        $percentage={(calculation.ltv / config.maxLTV) * 100}
                        $color={getGaugeColor(calculation.ltv, config.maxLTV)}
                      />
                    </GaugeContainer>
                  </MetricBox>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <MetricBox>
                    <MetricValue sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {formatRate(calculation.ltc)}
                    </MetricValue>
                    <MetricLabel>LTC (max {config.maxLTC}%)</MetricLabel>
                    <GaugeContainer>
                      <GaugeFill
                        $percentage={(calculation.ltc / config.maxLTC) * 100}
                        $color={getGaugeColor(calculation.ltc, config.maxLTC)}
                      />
                    </GaugeContainer>
                  </MetricBox>
                </Grid>
              </Grid>

              {/* Rate Adjustments */}
              {calculation.adjustments.length > 0 && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                    Rate Adjustments Applied
                  </Typography>
                  {calculation.adjustments.map((adj, index) => (
                    <AdjustmentItem key={index}>
                      <span>{adj.description}</span>
                      <span
                        style={{ color: adj.adjustment < 0 ? theme.palette.success.main : theme.palette.error.main }}
                      >
                        {adj.adjustment > 0 ? '+' : ''}
                        {formatRate(adj.adjustment)}
                      </span>
                    </AdjustmentItem>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <WarningText>{calculation.ineligibilityReason}</WarningText>
          )}

          <Disclaimer>
            Rates shown are estimates and subject to final underwriting approval. Actual rates may vary based on
            additional factors not captured here.
          </Disclaimer>
        </ResultsCard>
      )}
    </Box>
  );
};

RateCalculatorPanel.displayName = 'RateCalculatorPanel';

export default RateCalculatorPanel;
